import { createFileRoute } from "@tanstack/react-router";
import { useCallback } from "react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/Card/card";
import { FlowBoard } from "@/components/FlowNode";
import { VStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { FormWrapper } from "@/features/form-factory";
import { handleApiError } from "@/lib/api/error";
import { defaultNS, useTranslation } from "@/lib/i18n";

import {
  convertApprovalFlowToFlowData,
  convertSubmitTaskFormToFormDefinition,
  useApproveApplication,
  useGetApprovalTaskDetail,
} from "./-query";

export const Route = createFileRoute(
  "/_individual/individual/task/$id/approval/",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "common.action",
  });
  const { i18n } = useTranslation();
  const { id } = Route.useParams();
  const { data } = useGetApprovalTaskDetail(id);
  const navigate = Route.useNavigate();

  // 承認フローデータをFlowNodeData形式に変換
  const flowData = convertApprovalFlowToFlowData(data.approvalFlow);

  // 申請書データをFormWrapper用の形式に変換
  const formDefinition = convertSubmitTaskFormToFormDefinition(
    data.submitTaskForm,
  );

  const handleNavigateBack = useCallback(() => {
    navigate({ to: `/individual` });
  }, [navigate]);

  const { mutate: approveApplication, isPending } = useApproveApplication({
    onSuccess: () => {
      toast({
        description: "承認が完了しました",
        variant: "default",
      });
      navigate({ to: `/individual` });
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      toast({
        description: message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = useCallback(() => {
    approveApplication({
      taskId: id,
    });
  }, [approveApplication, id]);

  return (
    <div className="p-6">
      <VStack className="gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{data.approvalTaskCatalogName}</CardTitle>
          </CardHeader>
          <CardContent>
            <VStack className="gap-4">
              <div>
                <Text variant="labelLarge" className="mb-2">
                  申請書の内容
                </Text>
                {formDefinition ? (
                  <FormWrapper
                    formDefinition={formDefinition}
                    initialValues={formDefinition.formValues}
                    readOnly={true}
                  />
                ) : (
                  <Text variant="p" className="text-muted-foreground">
                    申請書データがありません
                  </Text>
                )}
              </div>

              <div>
                <Text variant="labelLarge" className="mb-2">
                  承認フロー
                </Text>
                <div className="h-96 border rounded">
                  <FlowBoard
                    data={flowData}
                    config={{
                      showControls: true,
                      showMiniMap: false,
                      enableInteraction: true,
                    }}
                    className="w-full h-full"
                  />
                </div>
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={handleNavigateBack}>
                  {t("cancel")}
                </Button>
                <Button onClick={handleSubmit} disabled={isPending}>
                  {isPending ? "承認中..." : "承認"}
                </Button>
              </div>
            </VStack>
          </CardContent>
        </Card>
      </VStack>
    </div>
  );
}
